import { useState } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Search,
  MapPin,
  ShieldCheck,
  Building2,
  Home,
  TreeDeciduous,
  Filter,
  X,
  Heart,
  Star,
  User,
  Grid,
  List,
  MessageCircle,
  Sparkles,
  Compass,
  ArrowUpDown
} from 'lucide-react';
import Logo from '../components/Logo';
import { useEffect } from 'react';
import SurveyModal from '../components/SurveyModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const propertyTypes = [
  { value: 'all', label: 'All Types' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'villa', label: 'Villa' },
  { value: 'land', label: 'Land' },
  { value: 'plot', label: 'Plot' },
  { value: 'project', label: 'Project' },
  { value: 'venture', label: 'Venture / Layout' }
];

const propertyTypeIcons = {
  apartment: Building2,
  villa: Home,
  land: TreeDeciduous,
  plot: TreeDeciduous,
  project: Building2,
  venture: Building2
};

export default function SearchPage() {
  const { customer, isCustomer, logout } = useAuth();
  const [showSurvey, setShowSurvey] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [premiumProperties, setPremiumProperties] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [availableAgencies, setAvailableAgencies] = useState([]);
  const [agencySearch, setAgencySearch] = useState('');

  const [filters, setFilters] = useState({
    property_type: searchParams.get('type') || 'all',
    location_city: searchParams.get('city') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    min_size: searchParams.get('min_size') || '',
    max_size: searchParams.get('max_size') || '',
    agency_name: '',
    min_trust_rating: '',
    sort_by: 'newest',
    radius_km: '',
    agency_id: ''
  });

  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    fetchProperties();
    fetchPremiumProperties();
    if (isCustomer) {
      fetchFavorites();
    }
  }, []);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/public/search?limit=50`;

      if (filters.property_type && filters.property_type !== 'all') {
        url += `&property_type=${filters.property_type}`;
      }
      if (filters.location_city) {
        url += `&location_city=${filters.location_city}`;
      }
      if (filters.min_price) {
        url += `&min_price=${filters.min_price}`;
      }
      if (filters.max_price) {
        url += `&max_price=${filters.max_price}`;
      }
      if (filters.min_size) {
        url += `&min_size=${filters.min_size}`;
      }
      if (filters.max_size) {
        url += `&max_size=${filters.max_size}`;
      }
      if (filters.agency_name) {
        url += `&agency_name=${filters.agency_name}`;
      }
      if (filters.min_trust_rating) {
        url += `&min_trust_rating=${filters.min_trust_rating}`;
      }
      if (filters.sort_by) {
        url += `&sort_by=${filters.sort_by}`;
      }
      if (filters.radius_km) {
        url += `&radius_km=${filters.radius_km}`;
      }
      if (userLocation) {
        url += `&lat=${userLocation.lat}&lng=${userLocation.lng}`;
      }

      if (filters.agency_id) {
        url += `&agency_id=${filters.agency_id}`;
      }

      const response = await axios.get(url);
      setProperties(response.data.properties);
      setTotal(response.data.total);
      setAvailableCities(response.data.available_cities || []);
      setAvailableAgencies(response.data.available_agencies || []);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPremiumProperties = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/premium-properties?limit=6`);
      setPremiumProperties(response.data.properties);
    } catch (error) {
      console.error('Failed to fetch premium properties:', error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/customer/favorites`, {
        headers: getAuthHeader()
      });
      setFavorites(response.data.properties.map(p => p.id));
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    }
  };

  const toggleFavorite = async (propertyId) => {
    if (!isCustomer) {
      toast.error('Please login to save favorites');
      return;
    }

    try {
      if (favorites.includes(propertyId)) {
        await axios.delete(`${API_URL}/api/customer/favorites/${propertyId}`, {
          headers: getAuthHeader()
        });
        setFavorites(prev => prev.filter(id => id !== propertyId));
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API_URL}/api/customer/favorites/${propertyId}`, {}, {
          headers: getAuthHeader()
        });
        setFavorites(prev => [...prev, propertyId]);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchProperties();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      property_type: 'all',
      location_city: '',
      min_price: '',
      max_price: '',
      min_size: '',
      min_size: '',
      max_size: '',
      agency_name: '',
      min_trust_rating: '',
      sort_by: 'newest',
      radius_km: '',
      agency_id: ''
    });
    setUserLocation(null);
  };

  const requestLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        toast.success('Location updated');
      },
      () => {
        toast.error('Failed to get location');
      }
    );
  };

  const formatPrice = (price) => {
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString()}`;
  };

  const hasActiveFilters = filters.property_type !== 'all' ||
    filters.location_city ||
    filters.min_price ||
    filters.max_price ||
    filters.min_size ||
    filters.max_price ||
    filters.min_size ||
    filters.max_size ||
    filters.agency_name ||
    filters.min_trust_rating;

  return (
    <div className="min-h-screen bg-[#FDFEFF] relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[1000px] h-[1000px] bg-sky-50/30 rounded-full blur-[160px] -z-10" />
      <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-[800px] h-[800px] bg-navy-50/40 rounded-full blur-[140px] -z-10" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[1200px] bg-sky-50/10 rounded-full blur-[200px] -z-10" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-navy-50/50 backdrop-blur-xl bg-white/70">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <Logo className="w-9 h-9" textClassName="text-xl" />
            </Link>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => setShowSurvey(true)}
                variant="outline"
                className="gap-2 border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 hover:text-sky-800"
              >
                <Sparkles className="w-4 h-4" />
                Smart Match
              </Button>
              {isCustomer ? (
                <>
                  <Link to="/customer/favorites">
                    <Button variant="outline" className="gap-2" data-testid="favorites-btn">
                      <Heart className="w-4 h-4" />
                      Favorites
                    </Button>
                  </Link>
                  <span className="text-navy-600">Hi, {customer?.name}</span>
                </>
              ) : (
                <>
                  <Link to="/customer/login">
                    <Button variant="ghost" data-testid="login-btn">Login</Button>
                  </Link>
                  <Link to="/customer/register">
                    <Button className="bg-navy-900 hover:bg-navy-800" data-testid="signup-btn">Sign Up</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Premium Properties Section */}
      {premiumProperties.length > 0 && (
        <section className="pt-24 pb-12 px-6 border-b border-navy-100 bg-white/40 backdrop-blur-sm">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex items-center gap-2 mb-6">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <h2 className="text-xl font-heading font-bold text-navy-900">Featured Properties</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {premiumProperties.slice(0, 3).map((property) => {
                const TypeIcon = propertyTypeIcons[property.property_type] || Building2;
                return (
                  <Link
                    key={property.id}
                    to={`/agency/${property.agency_slug}/property/${property.id}`}
                    state={{ from: '/search' }}
                    className="group"
                  >
                    <div className="relative h-48 rounded-lg overflow-hidden">
                      {property.images?.[0] ? (
                        <img src={getImageUrl(property.images[0])} alt={property.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full bg-navy-100 flex items-center justify-center">
                          <TypeIcon className="w-12 h-12 text-navy-300" />
                        </div>
                      )}
                      <Badge className="absolute top-3 left-3 bg-yellow-500 gap-1">
                        <Star className="w-3 h-3 fill-white" />
                        Premium
                      </Badge>
                    </div>
                    <h3 className="mt-3 font-semibold text-navy-900 group-hover:text-sky-500 transition-colors">{property.title}</h3>
                    <p className="text-sm text-navy-500">{property.location_area}, {property.location_city}</p>
                    <p className="text-lg font-bold text-navy-900 mt-1">{formatPrice(property.price)}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Search Content */}
      <div className={`max-w-[1600px] mx-auto px-6 py-8 ${premiumProperties.length === 0 ? 'pt-24' : ''}`}>
        <div className="flex flex-col lg:grid lg:grid-cols-[320px_1fr] gap-10">

          {/* Sidebar Filters */}
          <aside className={`${showFilters ? 'block' : 'hidden'} lg:block space-y-8`}>
            {/* Sort & Quick Actions */}
            <div className="bg-white p-6 rounded-xl shadow-soft border border-navy-100">
              <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4">Sort & View</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs text-navy-500">Sort results by</Label>
                  <div className="flex items-center gap-2 bg-navy-50 px-3 py-2 rounded-lg border border-navy-100">
                    <ArrowUpDown className="w-4 h-4 text-navy-400" />
                    <select
                      className="bg-transparent text-sm font-medium text-navy-700 focus:outline-none w-full"
                      value={filters.sort_by}
                      onChange={(e) => {
                        const val = e.target.value;
                        handleFilterChange('sort_by', val);
                        if (val === 'near_me' && !userLocation) {
                          requestLocation();
                        }
                      }}
                    >
                      <option value="newest">Newest First</option>
                      <option value="price_low">Price: Low to High</option>
                      <option value="price_high">Price: High to Low</option>
                      <option value="near_me">Near Me</option>
                    </select>
                  </div>
                </div>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    onClick={clearFilters}
                    className="w-full text-navy-500 hover:text-navy-700 hover:bg-navy-50 text-xs"
                  >
                    <X className="w-3 h-3 mr-2" />
                    Reset all filters
                  </Button>
                )}
              </div>
            </div>

            {/* Main Filters */}
            <div className="bg-white p-6 rounded-xl shadow-soft border border-navy-100 space-y-6">
              <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider">Property filters</h3>

              <div className="space-y-2">
                <Label>Location</Label>
                <Select
                  value={filters.location_city || 'all_cities'}
                  onValueChange={(v) => handleFilterChange('location_city', v === 'all_cities' ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select City" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_cities">All Cities</SelectItem>
                    {availableCities.map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Property Type</Label>
                <Select
                  value={filters.property_type}
                  onValueChange={(v) => handleFilterChange('property_type', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Property Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-4 border-t border-navy-50 space-y-4">
                <div className="space-y-2">
                  <Label>Price Range (₹)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      placeholder="Min"
                      className="text-xs"
                      value={filters.min_price}
                      onChange={(e) => handleFilterChange('min_price', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Max"
                      className="text-xs"
                      value={filters.max_price}
                      onChange={(e) => handleFilterChange('max_price', e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Size Range (sqft)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      placeholder="Min"
                      className="text-xs"
                      value={filters.min_size}
                      onChange={(e) => handleFilterChange('min_size', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Max"
                      className="text-xs"
                      value={filters.max_size}
                      onChange={(e) => handleFilterChange('max_size', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-navy-50 space-y-4">
                <div className="space-y-2">
                  <Label>Search Radius</Label>
                  <Select
                    value={filters.radius_km || 'any'}
                    onValueChange={(v) => {
                      handleFilterChange('radius_km', v === 'any' ? '' : v);
                      if (v !== 'any' && !userLocation) {
                        requestLocation();
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any Distance" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any Distance</SelectItem>
                      <SelectItem value="5">Within 5 km</SelectItem>
                      <SelectItem value="10">Within 10 km</SelectItem>
                      <SelectItem value="25">Within 25 km</SelectItem>
                      <SelectItem value="50">Within 50 km</SelectItem>
                    </SelectContent>
                  </Select>
                  {userLocation && (
                    <p className="text-[10px] text-green-600 flex items-center gap-1">
                      <Compass className="w-3 h-3" />
                      Proximity enabled
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Min Agency Rating</Label>
                  <Select
                    value={filters.min_trust_rating || 'any'}
                    onValueChange={(v) => handleFilterChange('min_trust_rating', v === 'any' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any Rating" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any Rating</SelectItem>
                      <SelectItem value="3">3+ Stars</SelectItem>
                      <SelectItem value="4">4+ Stars</SelectItem>
                      <SelectItem value="4.5">4.5+ Stars</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button onClick={applyFilters} className="w-full bg-navy-900 hover:bg-navy-800 shadow-lg shadow-navy-100">
                Apply Filters
              </Button>
            </div>

            {/* Agency List Filter */}
            {availableAgencies.length > 0 && (
              <div className="bg-white p-6 rounded-xl shadow-soft border border-navy-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider">Agencies</h3>
                </div>

                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-navy-400" />
                  <Input
                    placeholder="Search agencies..."
                    className="pl-9 h-9 text-xs bg-navy-50/50 border-navy-100 focus:bg-white transition-all"
                    value={agencySearch}
                    onChange={(e) => setAgencySearch(e.target.value)}
                  />
                  {agencySearch && (
                    <button
                      onClick={() => setAgencySearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>

                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                  <button
                    onClick={() => handleFilterChange('agency_id', '')}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${!filters.agency_id ? 'bg-sky-50 text-sky-700 font-semibold' : 'text-navy-600 hover:bg-navy-50'
                      }`}
                  >
                    <span>All Agencies</span>
                    <span className="text-xs opacity-60 text-navy-400">{total}</span>
                  </button>

                  {availableAgencies
                    .filter(agency => agency.name.toLowerCase().includes(agencySearch.toLowerCase()))
                    .map((agency) => (
                      <button
                        key={agency.id}
                        onClick={() => handleFilterChange('agency_id', agency.id)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-left transition-colors ${filters.agency_id === agency.id ? 'bg-sky-50 text-sky-700 font-semibold' : 'text-navy-600 hover:bg-navy-50'
                          }`}
                      >
                        <span className="truncate mr-2">{agency.name}</span>
                        <span className="text-xs opacity-60 text-navy-400 bg-navy-50 px-1.5 py-0.5 rounded-full">{agency.count}</span>
                      </button>
                    ))}

                  {availableAgencies.filter(agency => agency.name.toLowerCase().includes(agencySearch.toLowerCase())).length === 0 && (
                    <p className="text-center py-4 text-xs text-navy-400 italic">No agencies found</p>
                  )}
                </div>
              </div>
            )}
          </aside>

          {/* Main Results Area */}
          <main className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-heading font-bold text-navy-900">
                  Properties for You
                </h1>
                <p className="text-navy-500 text-sm">{total} premium listings found</p>
              </div>
              <Button
                variant="outline"
                className="lg:hidden gap-2"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4" />
                Filters
              </Button>
            </div>

            {/* Properties Grid */}
            {loading ? (
              <div className="grid md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="h-56 bg-navy-200 rounded-xl mb-4" />
                    <div className="h-6 bg-navy-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-navy-200 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : properties.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-navy-200">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-navy-200" />
                <h3 className="text-lg font-semibold text-navy-900 mb-2">No properties match your criteria</h3>
                <p className="text-navy-500 mb-6 max-w-xs mx-auto">Try broadening your search or resetting the filters.</p>
                <Button variant="outline" onClick={clearFilters}>
                  Clear All Filters
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-8">
                {properties.map((property, index) => {
                  const TypeIcon = propertyTypeIcons[property.property_type] || Building2;
                  const isFavorite = favorites.includes(property.id);
                  return (
                    <motion.div
                      key={property.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-float border border-navy-50 transition-all duration-300"
                    >
                      <div className="relative h-56">
                        <Link
                          to={`/agency/${property.agency_slug}/property/${property.id}`}
                          state={{ from: '/search' }}
                        >
                          {property.images?.[0] ? (
                            <img
                              src={getImageUrl(property.images[0])}
                              alt={property.title}
                              className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                            />
                          ) : (
                            <div className="w-full h-full bg-navy-50 flex items-center justify-center">
                              <TypeIcon className="w-12 h-12 text-navy-200" />
                            </div>
                          )}
                        </Link>
                        <button
                          onClick={() => toggleFavorite(property.id)}
                          className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md transition-all ${isFavorite ? 'bg-red-500 text-white' : 'bg-white/80 text-navy-600 hover:bg-red-50 hover:text-red-500'
                            }`}
                        >
                          <Heart className={`w-5 h-5 ${isFavorite ? 'fill-white' : ''}`} />
                        </button>
                        {property.is_verified && (
                          <Badge className="bg-sky-600/90 backdrop-blur-sm border-none px-3 py-1">
                            <ShieldCheck className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                        {property.is_sponsored && (
                          <Badge className="bg-amber-500/90 backdrop-blur-sm border-none px-3 py-1">
                            <Star className="w-3 h-3 mr-1 fill-white" />
                            Sponsored
                          </Badge>
                        )}
                        {property.distance_km && (
                          <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-bold text-navy-900 flex items-center gap-1 shadow-sm">
                            <MapPin className="w-3 h-3 text-sky-500" />
                            {property.distance_km} km away
                          </div>
                        )}
                      </div>

                      <Link
                        to={`/agency/${property.agency_slug}/property/${property.id}`}
                        state={{ from: '/search' }}
                      >
                        <div className="p-5">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-bold text-navy-900 line-clamp-1 group-hover:text-sky-600 transition-colors">
                              {property.title}
                            </h3>
                          </div>
                          <p className="text-sm text-navy-500 flex items-center gap-1 mb-4">
                            <MapPin className="w-3.5 h-3.5 text-sky-400" />
                            {property.location_area}, {property.location_city}
                          </p>

                          <div className="flex items-center justify-between pt-4 border-t border-navy-50">
                            <div>
                              <p className="text-lg font-black text-navy-900">
                                {property.property_type === 'venture' && property.price_min && property.price_max
                                  ? `${formatPrice(property.price_min)} - ${formatPrice(property.price_max)}`
                                  : formatPrice(property.price)
                                }
                              </p>
                              <p className="text-[10px] font-medium text-navy-400 uppercase tracking-wider">
                                {property.size} {property.size_unit} • {property.property_type}
                              </p>
                            </div>
                            {property.agency_phone && (
                              <a
                                href={`https://wa.me/${property.agency_phone}?text=Hi, I'm interested in ${property.title}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="w-10 h-10 bg-green-500 hover:bg-green-600 text-white rounded-full transition-all flex items-center justify-center shadow-lg shadow-green-100 hover:scale-110"
                                title="Chat on WhatsApp"
                              >
                                <MessageCircle className="w-5 h-5" />
                              </a>
                            )}
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>
      <SurveyModal isOpen={showSurvey} onClose={() => setShowSurvey(false)} />
    </div>
  );
}
